import { isAxiosError } from 'axios';
import {
	BehaviorSubject,
	combineLatest,
	defer,
	EMPTY,
	Observable,
	Subject,
	Subscription,
	timer,
} from 'rxjs';
import {
	catchError,
	distinctUntilChanged,
	exhaustMap,
	filter,
	finalize,
	retry,
	switchMap,
	takeUntil,
	takeWhile,
	tap,
} from 'rxjs/operators';

export const IN_PROGRESS_POLLING_INTERVAL_MS = 1500;
export const SUCCESS_POLLING_INTERVAL_MS = 5000;

export const POLLING_RETRY_COUNT = 3;
export const POLLING_RETRY_DELAY_MS = 1000;

/** HTTP codes a poll cannot recover from by retrying. */
export const POLL_NON_RETRYABLE_HTTP_CODES = [404, 410];

/** True only for a 404/410 HTTP error. Transport errors and other codes are retryable. */
export function isNonRetryablePollError(err: unknown): boolean {
	const status = isAxiosError(err) && err.response?.status;

	return (
		typeof status === 'number' && POLL_NON_RETRYABLE_HTTP_CODES.includes(status)
	);
}

export interface ICapacityCheckPollerConfig<TRes> {
	/** Runs one poll for the id and returns the raw result. Owns the full request lifecycle. */
	poll: (capacityCheckId: string) => Promise<TRes>;

	/** Next interval derived from a result, or `null` to keep the current one. */
	getNextInterval: (res: TRes) => number | null;

	/** True when the result is terminal and the loop should stop. */
	shouldStop: (res: TRes) => boolean;

	/** Called when retries are exhausted or a non-retryable error surfaces. */
	onError: (err: unknown) => void;

	/** Marks an error as non-retryable. Defaults to {@link isNonRetryablePollError}. */
	isNonRetryable?: (err: unknown) => boolean;

	/** Delay before the first tick of a run. Default {@link IN_PROGRESS_POLLING_INTERVAL_MS}. */
	firstTickDelayMs?: number;

	/** Starting interval, also the {@link CapacityCheckPoller.resetInterval} target. Default {@link IN_PROGRESS_POLLING_INTERVAL_MS}. */
	inProgressIntervalMs?: number;

	/** Retry attempts on a transient failure. Default {@link POLLING_RETRY_COUNT}. */
	retryCount?: number;

	/** Delay between retries. Default {@link POLLING_RETRY_DELAY_MS}. */
	retryDelayMs?: number;
}

/**
 * Self-adjusting polling loop: polls on a timer, retries transient failures, self-stops
 * on a terminal result, and can be paused. Data-agnostic — result semantics live entirely
 * in the {@link ICapacityCheckPollerConfig} callbacks.
 */
export class CapacityCheckPoller<TRes> {
	private readonly firstTickDelayMs: number;
	private readonly inProgressIntervalMs: number;
	private readonly retryCount: number;
	private readonly retryDelayMs: number;

	private readonly interval$: BehaviorSubject<number>;
	private readonly paused$ = new BehaviorSubject<boolean>(false);
	/** `.next()` stops the current run; `.complete()` disposes permanently. */
	private readonly stop$ = new Subject<void>();
	private readonly isPolling$ = new BehaviorSubject<boolean>(false);

	private outerSub?: Subscription;
	private disposed = false;

	constructor(private readonly cfg: ICapacityCheckPollerConfig<TRes>) {
		this.firstTickDelayMs =
			cfg.firstTickDelayMs ?? IN_PROGRESS_POLLING_INTERVAL_MS;
		this.inProgressIntervalMs =
			cfg.inProgressIntervalMs ?? IN_PROGRESS_POLLING_INTERVAL_MS;
		this.retryCount = cfg.retryCount ?? POLLING_RETRY_COUNT;
		this.retryDelayMs = cfg.retryDelayMs ?? POLLING_RETRY_DELAY_MS;

		this.interval$ = new BehaviorSubject<number>(this.inProgressIntervalMs);
	}

	/**
	 * Starts (or restarts) the loop against a stream of ids. Tears down any prior run
	 * synchronously first, so its late `finalize` cannot clip the new one. No-op once disposed.
	 */
	public start(capacityCheckId$: Observable<string | null | undefined>): void {
		if (this.disposed) {
			return;
		}

		this.teardownCurrentRun();

		/** Fresh run starts unpaused — a stale pause must not drop every tick. */
		this.paused$.next(false);

		const interval$ = this.interval$.pipe(distinctUntilChanged());
		const id$ = capacityCheckId$.pipe(distinctUntilChanged());

		this.outerSub = combineLatest([interval$, id$])
			.pipe(
				takeUntil(this.stop$),
				switchMap(([intervalDuration, capacityCheckId]) => {
					if (!capacityCheckId) {
						this.isPolling$.next(false);
						return EMPTY;
					}

					this.isPolling$.next(true);

					return timer(this.firstTickDelayMs, intervalDuration).pipe(
						/**
						 * Pause gate inside the timer pipe: paused ticks are dropped without
						 * unsubscribing the timer. Unsubscribing would fire `finalize` →
						 * `stop$.next()` and stop the loop permanently.
						 */
						filter(() => !this.paused$.getValue()),
						exhaustMap(() =>
							defer(() => this.cfg.poll(capacityCheckId)).pipe(
								/** Retry transient failures; a non-retryable error skips retries and bubbles out. */
								retry({
									count: this.retryCount,
									delay: (err) => {
										if (
											(this.cfg.isNonRetryable ?? isNonRetryablePollError)(err)
										) {
											throw err;
										}

										return timer(this.retryDelayMs);
									},
								})
							)
						),
						tap((res) => {
							const next = this.cfg.getNextInterval(res);
							if (next != null) {
								this.interval$.next(next);
							}
						}),
						takeWhile((res) => !this.cfg.shouldStop(res), true),
						takeUntil(this.stop$),
						catchError((err) => {
							this.cfg.onError(err);
							return EMPTY;
						}),
						finalize(() => {
							this.isPolling$.next(false);
							this.stop$.next();
						})
					);
				})
			)
			.subscribe();
	}

	/** Stops the current run. Restartable via {@link start}. */
	public stop(): void {
		this.stop$.next();
		this.teardownCurrentRun();
	}

	/** Drops ticks until {@link resume}. */
	public pause(): void {
		this.paused$.next(true);
	}

	public resume(): void {
		this.paused$.next(false);
	}

	/** Returns the interval to {@link inProgressIntervalMs}. */
	public resetInterval(): void {
		this.interval$.next(this.inProgressIntervalMs);
	}

	/** Permanent teardown — not restartable afterwards. */
	public dispose(): void {
		this.stop$.next();
		this.stop$.complete();
		this.teardownCurrentRun();
		this.disposed = true;
	}

	/** Test-only introspection: is a poll armed right now? */
	public get isPolling(): boolean {
		return this.isPolling$.getValue();
	}

	private teardownCurrentRun(): void {
		this.outerSub?.unsubscribe();
		this.outerSub = undefined;
	}
}
